import React, { useState } from 'react';
import { LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';

const AnalyticsDashboard = ({ analytics, pressKitElements }) => {
  const [timeRange, setTimeRange] = useState('7d'); // 1d, 7d, 30d, 90d
  const [activeMetric, setActiveMetric] = useState('views');

  // Mock analytics data for demonstration
  const analyticsData = {
    overview: {
      totalViews: 1247,
      uniqueVisitors: 892,
      avgSessionDuration: '2m 34s',
      bounceRate: '23%',
      downloadRate: '7.1%',
      shareRate: '4.2%'
    },
    trends: {
      '7d': [
        { date: '2024-03-08', views: 123, downloads: 8, shares: 3 },
        { date: '2024-03-09', views: 156, downloads: 12, shares: 5 },
        { date: '2024-03-10', views: 189, downloads: 15, shares: 7 },
        { date: '2024-03-11', views: 145, downloads: 9, shares: 4 },
        { date: '2024-03-12', views: 178, downloads: 13, shares: 6 },
        { date: '2024-03-13', views: 234, downloads: 18, shares: 9 },
        { date: '2024-03-14', views: 222, downloads: 14, shares: 8 }
      ]
    },
    topElements: [
      { id: 'author-bio', name: 'Author Biography', views: 456, clicks: 89, engagement: 87 },
      { id: 'book-cover', name: 'Book Cover', views: 423, clicks: 156, engagement: 92 },
      { id: 'press-release', name: 'Press Release', views: 389, clicks: 67, engagement: 73 },
      { id: 'media-gallery', name: 'Media Gallery', views: 334, clicks: 134, engagement: 89 },
      { id: 'social-feed', name: 'Social Media', views: 278, clicks: 45, engagement: 65 }
    ],
    demographics: [
      { name: 'Literary Professionals', value: 35, color: '#3b82f6' },
      { name: 'Media & Journalists', value: 28, color: '#8b5cf6' },
      { name: 'General Readers', value: 22, color: '#10b981' },
      { name: 'Industry Influencers', value: 15, color: '#f59e0b' }
    ],
    geoData: [
      { country: 'United States', views: 487, percentage: 39 },
      { country: 'United Kingdom', views: 234, percentage: 19 },
      { country: 'Canada', views: 156, percentage: 12 },
      { country: 'Australia', views: 123, percentage: 10 },
      { country: 'Germany', views: 89, percentage: 7 }
    ],
    referrers: [
      { source: 'Direct Traffic', views: 445, percentage: 36 },
      { source: 'Social Media', views: 298, percentage: 24 },
      { source: 'Search Engines', views: 267, percentage: 21 },
      { source: 'Email Campaigns', views: 156, percentage: 13 },
      { source: 'Referral Sites', views: 81, percentage: 6 }
    ]
  };

  // Time range options
  const timeRanges = [
    { id: '1d', label: 'Last 24h' },
    { id: '7d', label: 'Last 7 days' },
    { id: '30d', label: 'Last 30 days' },
    { id: '90d', label: 'Last 90 days' }
  ];

  // Metric options
  const metrics = [
    { id: 'views', label: 'Views', icon: 'Eye', color: 'bg-blue-500' },
    { id: 'downloads', label: 'Downloads', icon: 'Download', color: 'bg-green-500' },
    { id: 'shares', label: 'Shares', icon: 'Share2', color: 'bg-purple-500' },
    { id: 'engagement', label: 'Engagement', icon: 'MousePointer', color: 'bg-orange-500' }
  ];

  const formatNumber = (num) => {
    if (num >= 1000) {
      return (num / 1000)?.toFixed(1) + 'K';
    }
    return num?.toString();
  };

  return (
    <div className="w-80 bg-white border-r border-slate-200 flex flex-col">
      {/* Analytics Header */}
      <div className="p-4 border-b border-slate-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-slate-900">Analytics Dashboard</h3>
          <div className="flex items-center space-x-1">
            <button className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
              <Icon name="Download" size={14} className="text-slate-600" />
            </button>
            <button className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
              <Icon name="Settings" size={14} className="text-slate-600" />
            </button>
          </div>
        </div>

        {/* Time Range Selector */}
        <div className="flex items-center space-x-1 bg-slate-100 rounded-lg p-1">
          {timeRanges?.map((range) => (
            <button
              key={range?.id}
              onClick={() => setTimeRange(range?.id)}
              className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                timeRange === range?.id
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {range?.label}
            </button>
          ))}
        </div>
      </div>

      {/* Key Metrics Overview */}
      <div className="p-4 border-b border-slate-200">
        <h4 className="text-sm font-medium text-slate-900 mb-3">Key Metrics</h4>
        <div className="grid grid-cols-2 gap-3">
          {[
            { label: 'Total Views', value: analyticsData?.overview?.totalViews, icon: 'Eye', color: 'text-blue-600' },
            { label: 'Downloads', value: analytics?.downloads, icon: 'Download', color: 'text-green-600' },
            { label: 'Avg. Duration', value: analyticsData?.overview?.avgSessionDuration, icon: 'Clock', color: 'text-purple-600' },
            { label: 'Engagement', value: `${analytics?.engagement}%`, icon: 'TrendingUp', color: 'text-orange-600' }
          ]?.map((metric, index) => (
            <div key={index} className="p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center space-x-2 mb-1">
                <Icon name={metric?.icon} size={14} className={metric?.color} />
                <span className="text-xs text-slate-600">{metric?.label}</span>
              </div>
              <div className="text-lg font-bold text-slate-900">{formatNumber(metric?.value)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Performance Chart */}
      <div className="p-4 border-b border-slate-200">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-slate-900">Performance Trends</h4>
          <div className="flex items-center space-x-1">
            {metrics?.map((metric) => (
              <button
                key={metric?.id}
                onClick={() => setActiveMetric(metric?.id)}
                className={`p-1 rounded transition-colors ${
                  activeMetric === metric?.id ? metric?.color : 'bg-slate-200'
                }`}
              >
                <Icon name={metric?.icon} size={12} className="text-white" />
              </button>
            ))}
          </div>
        </div>
        
        <div className="h-32">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={analyticsData?.trends?.[timeRange]}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 10 }}
                tickFormatter={(date) => new Date(date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#ffffff', 
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '12px'
                }}
              />
              <Line 
                type="monotone" 
                dataKey={activeMetric} 
                stroke="#3b82f6" 
                strokeWidth={2}
                dot={{ fill: '#3b82f6', strokeWidth: 0, r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top Performing Elements */}
      <div className="p-4 border-b border-slate-200">
        <h4 className="text-sm font-medium text-slate-900 mb-3">Element Performance</h4>
        <div className="space-y-2">
          {analyticsData?.topElements?.slice(0, 3)?.map((element, index) => (
            <div key={element?.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
              <div className="flex items-center space-x-2">
                <div className={`w-2 h-2 rounded-full ${
                  index === 0 ? 'bg-green-500' : index === 1 ? 'bg-blue-500' : 'bg-purple-500'
                }`} />
                <span className="text-xs font-medium text-slate-700">{element?.name}</span>
              </div>
              <div className="text-right">
                <div className="text-xs font-bold text-slate-900">{element?.engagement}%</div>
                <div className="text-xs text-slate-500">{formatNumber(element?.views)} views</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Audience Demographics */}
      <div className="p-4 border-b border-slate-200">
        <h4 className="text-sm font-medium text-slate-900 mb-3">Audience Demographics</h4>
        <div className="h-24 mb-3">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={analyticsData?.demographics}
                cx="50%"
                cy="50%"
                outerRadius={40}
                dataKey="value"
              >
                {analyticsData?.demographics?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry?.color} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#ffffff', 
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '12px'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="space-y-1">
          {analyticsData?.demographics?.map((demo, index) => (
            <div key={index} className="flex items-center justify-between text-xs">
              <div className="flex items-center space-x-2">
                <div 
                  className="w-2 h-2 rounded-full" 
                  style={{ backgroundColor: demo?.color }}
                />
                <span className="text-slate-600">{demo?.name}</span>
              </div>
              <span className="font-medium text-slate-900">{demo?.value}%</span>
            </div>
          ))}
        </div>
      </div>

      {/* Traffic Sources */}
      <div className="flex-1 overflow-y-auto p-4">
        <h4 className="text-sm font-medium text-slate-900 mb-3">Traffic Sources</h4>
        <div className="space-y-2">
          {analyticsData?.referrers?.map((referrer, index) => (
            <div key={index} className="p-2 border border-slate-200 rounded-lg">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-slate-700">{referrer?.source}</span>
                <span className="text-xs font-bold text-slate-900">{referrer?.percentage}%</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-1">
                <div 
                  className="bg-indigo-500 h-1 rounded-full transition-all duration-300" 
                  style={{ width: `${referrer?.percentage}%` }}
                />
              </div>
              <div className="text-xs text-slate-500 mt-1">{formatNumber(referrer?.views)} views</div>
            </div>
          ))}
        </div>

        {/* Geographic Distribution */}
        <h4 className="text-sm font-medium text-slate-900 mb-3 mt-6">Geographic Distribution</h4>
        <div className="space-y-2">
          {analyticsData?.geoData?.slice(0, 5)?.map((geo, index) => (
            <div key={index} className="flex items-center justify-between text-xs">
              <span className="text-slate-600">{geo?.country}</span>
              <div className="flex items-center space-x-2">
                <span className="font-medium text-slate-900">{geo?.percentage}%</span>
                <span className="text-slate-500">({formatNumber(geo?.views)})</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Export & Report Actions */}
      <div className="p-4 border-t border-slate-200 space-y-2">
        <button className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm">
          <Icon name="FileDown" size={14} />
          <span>Export Report</span>
        </button>
        
        <div className="grid grid-cols-2 gap-2">
          <button className="flex items-center justify-center space-x-1 px-3 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors text-sm">
            <Icon name="Mail" size={12} />
            <span>Email</span>
          </button>
          <button className="flex items-center justify-center space-x-1 px-3 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors text-sm">
            <Icon name="Calendar" size={12} />
            <span>Schedule</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;